// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Editor.js
//
// An wrapper for renderer.js to handle and distribute mouse & touch events
// to editable.eventHandler instances.
import * as Mousetrap from 'mousetrap';
import type { RendererOptions } from './renderer';
import Renderer from './renderer';
import SVGInteraction from './SVGInteraction';
import utils from '../utils';
import NoteHolder from './noteHolder';
import type Editable from './editable';
import type Music from './music';
import type EventHandler from './eventHandler';

export interface EditorOptions extends RendererOptions {
  /** Disable screen reader only <input type=text>, default false */
  disableTextInput?: boolean;
  /** Maximum number of leger lines allowed for dragging & dropping */
  maxLegerLines?: number;
}

// GCR TODO: Rewrite using SVGInteraction class
const debug = false;
const log = debug
  ? (...args) => {
      console.log(...args);
    }
  : () => {};

export default class Editor {
  options: Partial<EditorOptions>;

  /** Maximum number of leger lines allowed in drag/hovering */
  maxLegerLines = 2;

  /** Editable objects for the various noteHolders rendered */
  editables: Editable[] = [];

  /** Editables that were active at the last render */
  priorEditables: Editable[] = [];

  /**
   * Editables with screen-reader only text input available.
   * There may be more of these than active on-screen editables
   * when the number of editables is fixed (as, for instance in
   * scale write, where we render all 8 accessible text input
   * boxes right away, but only have one active empty editable
   * on screen at any time.)
   */
  accessibleEditables: Editable[] = [];

  /**
   * Last time this was rendered, had we already rendered this
   * editable's accessible text input box? If so, don't re-render
   * it, so we don't lose its input.
   */
  priorAccessibleEditables: Editable[] = [];

  /** The div into which notation is rendered */
  vexFlowDiv: HTMLDivElement;

  /** The div where accessible text input boxes are rendered */
  accessibilityDiv: HTMLDivElement;

  renderer: Renderer;

  intersections: Editable[] = [];

  priorIntersections: Editable[] = [];

  svgInteraction: SVGInteraction;

  listeners: { type: string; callback: Function }[];

  /** disable keyboard listeners (i.e., up arrow, down arrow, etc..) */
  keyboardDisabled = false;

  /** Index to the editable that currently has keyboardFocus */
  keyboardFocusIndex = 0;

  /** Externally accessible trigger functions to simulate keyboard events */
  keyboardTriggers: { [key: string]: Function };

  /**
   *
   * @param {Music} music
   * @param {HTMLDivElement} vexFlowDiv
   * @param {Object} options
   * @param {boolean=} [options.disableTextInput] default false
   */
  constructor(music: Music, vexFlowDiv: HTMLDivElement, options: Partial<EditorOptions>) {
    this.options = options || {};
    if (typeof options.maxLegerLines === 'number') {
      this.maxLegerLines = options.maxLegerLines;
    }

    this.vexFlowDiv = vexFlowDiv;
    this.renderer = new Renderer(music, vexFlowDiv, options);
    this.initializeSvg();
    if (!options.disableTextInput) this.initializeTextInput();

    this.renderer.makeEditable(this);
    this.renderer.render();
    this.priorIntersections = [];
    this.priorEditables = [];
    this.initializeEvents();
    //  this.initializeEvents(this.vexFlowDiv);

    this.listeners = [];
    this.initArrowListeners();
    this.initializeAriaInstructions();
  }

  initArrowListeners() {
    const div = this.renderer.ctx.element;
    div.setAttribute('tabindex', '0');
    div.addEventListener('onfocus', () => {
      log('focused!');
    });
    const wrap = (fn) => () => {
      if (this.keyboardDisabled) return;
      if (!this.editables || !this.editables.length) return;
      // If something is selected, it has keyboard focus first...
      const editable =
        this.editables.find((selectedEditable) => selectedEditable.selected) ||
        this.editables[this.keyboardFocusIndex] ||
        this.editables[0];
      this.keyboardFocusIndex = this.editables.indexOf(editable);

      if (!editable) return;
      fn(editable.eventHandler);
    };

    const left = (handler: EventHandler) => {
      if (handler.selectPriorNextTopOrBottomNote('prior')) this.renderer.render();
      this.triggerListeners('left');
    };
    const right = (handler: EventHandler) => {
      if (handler.selectPriorNextTopOrBottomNote('next')) this.renderer.render();
      this.triggerListeners('right');
    };
    const up = (handler: EventHandler) => {
      log('up!');
      if (handler.moveUpOrDown('up')) this.renderer.render();
      this.triggerListeners('up');
    };
    const down = (handler: EventHandler) => {
      if (handler.moveUpOrDown('down')) this.renderer.render();
      this.triggerListeners('down');
    };
    const shiftUp = (handler: EventHandler) => {
      if (handler.addUpOrDown('up')) this.renderer.render();
      this.triggerListeners('shiftUp');
    };
    const shiftDown = (handler: EventHandler) => {
      if (handler.addUpOrDown('down')) this.renderer.render();
      this.triggerListeners('shiftDown');
    };

    const shiftLeftOrRight = (direction) => {
      // If something is selected, it has keyboard focus first...
      const editable =
        this.editables.find((selectedEditable) => selectedEditable.selected) ||
        this.editables[this.keyboardFocusIndex] ||
        this.editables[0];
      this.keyboardFocusIndex = this.editables.indexOf(editable);

      const increment = direction === 'left' ? -1 : 1;
      this.keyboardFocusIndex += increment;
      this.keyboardFocusIndex =
        this.keyboardFocusIndex < 0
          ? this.editables.length - 1
          : this.keyboardFocusIndex % this.editables.length;
      if (this.editables[this.keyboardFocusIndex].eventHandler.keyboardFocusIn())
        this.renderer.render();
      if (direction === 'left') {
        this.triggerListeners('shiftLeft');
      } else this.triggerListeners('shiftRight');
    };

    const bind = () => {
      Mousetrap.bind('up', wrap(up));
      Mousetrap.bind('down', wrap(down));
      Mousetrap.bind('left', wrap(left));
      Mousetrap.bind('right', wrap(right));
      Mousetrap.bind('shift+up', wrap(shiftUp));
      Mousetrap.bind('shift+down', wrap(shiftDown));
      Mousetrap.bind('shift+left', () => shiftLeftOrRight('left'));
      Mousetrap.bind('shift+right', () => shiftLeftOrRight('right'));
    };

    this.keyboardTriggers = {
      up: wrap(up),
      down: wrap(down),
      left: wrap(left),
      right: wrap(right),
      'shift+up': wrap(shiftUp),
      'shift+down': wrap(shiftDown),
      'shift+left': () => shiftLeftOrRight('left'),
      'shift+right': () => shiftLeftOrRight('right'),
    };

    // GCR TODO: right now we're not actually binding to any element.
    // Mousetrap is helping us and binding globally instead.
    bind();
  }

  initializeSvg() {
    const svg = this.renderer.ctx.svg;
    const svgInteraction = new SVGInteraction(svg);
    svgInteraction.makeInteractive();
    this.svgInteraction = svgInteraction;
  }

  clearEditables() {
    this.editables = [];
    this.priorAccessibleEditables = this.accessibleEditables;
    this.accessibleEditables = [];
  }

  addEditable(editable) {
    editable.setRenderer(this.renderer);
    editable.eventHandler.setParentListener(this.triggerListeners, this);
    this.editables.push(editable);
  }

  addAccessibleEditable(editable: Editable) {
    if (editable.options.pitch) return;
    if (this.options.disableTextInput) return;
    this.accessibleEditables.push(editable);
  }

  initializeAriaInstructions() {
    const accessibilityInstructionsDiv = document.createElement('div');
    accessibilityInstructionsDiv.innerHTML = `
<details>
  <summary
  role="button"
  aria-expanded="false"
  onclick="function toggleAriaExpanded() {
    this.setAttribute('aria-expanded', this.getAttribute('aria-expanded') === 'false' ? 'true' : 'false');
  }">
    Key bindings for editing musical notation
  </summary>
  <div>
    The following keys are available to edit music notation using the keyboard.
    Focus on the notation element, then:
    <ul>
      <li>Arrow up or down to change a note</li>
      <li>
        Arrow left or right to select the prior or next note within a chord
      </li>
      <li>
        Shift arrow left or right to move to the prior or next note or chord
      </li>
      <li>
        Shift arrow up or down to add anoter note above or below the selected
        note in a chord
      </li>
    </ul>
  </div>
</details>


    `;
    accessibilityInstructionsDiv.classList.add('sr-only');
    this.renderer.ctx.element.appendChild(accessibilityInstructionsDiv);
  }

  initializeTextInput() {
    if (this.options.disableTextInput) return;
    const accessibilityDiv = document.createElement('div');
    accessibilityDiv.classList.add('sr-only');
    this.renderer.div.appendChild(accessibilityDiv);
    this.accessibilityDiv = accessibilityDiv;
  }

  renderTextInputs() {
    if (this.options.disableTextInput) return;
    const currents = this.accessibleEditables;
    const priors = this.priorAccessibleEditables.filter((prior) => {
      if (currents.indexOf(prior) === -1) {
        this.accessibilityDiv.removeChild(prior.accessibleInputDiv);
        return false;
      }
      return true;
    });

    const firstBadIndex = priors.findIndex((prior, index) => index !== currents.indexOf(prior));
    if (firstBadIndex !== -1) {
      priors
        .slice(firstBadIndex)
        .forEach(
          (prior) =>
            prior.accessibleInputDiv &&
            prior.accessibleInputDiv.parentElement === this.accessibilityDiv &&
            this.accessibilityDiv.removeChild(prior.accessibleInputDiv)
        );
    }

    (firstBadIndex === -1 ? currents : currents.slice(firstBadIndex)).forEach((newEditable) => {
      if (newEditable.accessibleInputDiv && newEditable.options.pitch) {
        this.accessibilityDiv.appendChild(newEditable.accessibleInputDiv);
      } else {
        const accessibleInputDiv = this.renderTextInput(newEditable);
        if (accessibleInputDiv) this.accessibilityDiv.appendChild(accessibleInputDiv);
      }
    });

    // Now update the values & labels!
    let currentCount = 1;
    currents.forEach((editable) => {
      if (!editable.options.pitch) return;
      const nh = editable.noteHolder.clone();
      editable.options.fixedNotes.forEach((note) => nh.remove(note));
      (editable.accessibleInputDiv.lastChild as HTMLInputElement).value = nh.isEmpty()
        ? ''
        : nh.toString();

      const label = editable.accessibleInputDiv.firstChild;
      label.textContent = '';

      const fixedNotes = editable.options.fixedNotes;
      const fixedNoteNames = fixedNotes.map((note) => note.toString()).join('');
      const additionalNotes = editable.options.maxNotes - fixedNotes.length;
      const separation =
        additionalNotes > 1 ? ' Separate notes with a hyphen. For instance: C#4-E4-G#4' : '';
      // log('fixedNotes.lengt', fixedNotes.length);
      if (fixedNotes.length) {
        label.textContent += `Given note${fixedNotes.length > 1 ? 's:' : ''} ${fixedNoteNames}.`;
      }
      const chordOrNote = editable.options.maxNotes + fixedNotes.length > 1 ? 'Chord' : 'Note';
      label.textContent += `
      ${chordOrNote} ${currentCount++}: Input up to ${additionalNotes}${separation}
      ${fixedNotes.length ? ' additional' : ''}
      note${additionalNotes > 1 ? 's' : ''}.`;
    });
  }

  renderTextInput(editable: Editable) {
    if (this.options.disableTextInput) return false;
    const uuid = utils.uuid;
    const holderDiv = document.createElement('div');
    const input = document.createElement('input');
    input.classList.add('sr-only');
    const id = `note-text-input${uuid}`;
    input.id = id;
    const label = document.createElement('label');
    label.htmlFor = id;
    // Text input for ${ editable.clef.clefName } clef.

    input.onchange = () => {
      log(`changing to${input.value}`);
      try {
        const nh = NoteHolder.fromString(input.value);
        editable.noteHolder.notes = editable.options.fixedNotes.concat(nh.notes);
        this.renderer.render();
      } catch (err) {
        log('That is not a valid music string.');
      }
    };
    holderDiv.appendChild(label);
    holderDiv.appendChild(input);
    editable.accessibleInputDiv = holderDiv;

    return holderDiv;
  }

  // Removes all event listeners & destroys editing capabilities.
  disableEditor() {
    const divClone = this.vexFlowDiv.cloneNode(true);
    this.vexFlowDiv.parentNode.replaceChild(divClone, this.vexFlowDiv);
  }

  // ## Mouse & Touch Helpers:
  // ========================

  initializeEvents() {
    this.svgInteraction.addEventListener('touchStart', this.contactEventStart.bind(this));
    this.svgInteraction.addEventListener('touchEnd', this.contactEventEnd.bind(this));
    this.svgInteraction.addEventListener('drag', this.drag.bind(this));
    this.svgInteraction.addEventListener('hover', this.hover.bind(this));
    this.svgInteraction.addEventListener('mouseOut', this.mouseOut.bind(this));
  }

  getIntersections(coords) {
    const intersects = (needlePoint, haystackBox) =>
      needlePoint.x >= haystackBox.x &&
      needlePoint.x <= haystackBox.x + haystackBox.w &&
      needlePoint.y >= haystackBox.y &&
      needlePoint.y <= haystackBox.y + haystackBox.h;

    return this.editables.filter((editable) => {
      const box = editable.getBoundingBox();
      return intersects(coords, box);
    });
  }

  // ## Events:
  // ========================

  contactEventStart(evt, coords) {
    if (coords.touches && coords.touches.length > 1) return;
    // evt.stopPropagation();
    // evt.preventDefault();

    //      this.logEvent("contactEventStart", evt);
    let redrawNeeded = false;

    // deselect all
    redrawNeeded = this.deselectAll(true) || redrawNeeded;
    // check intersection and fire event
    const intersections = this.getIntersections(coords);
    intersections.forEach((editable) => {
      redrawNeeded = editable.eventHandler.contactEventStart(coords) || redrawNeeded;
    }, this);
    if (redrawNeeded) this.redraw();
    this.triggerListeners('contactEventStart');
  }

  contactEventEnd(evt, coords) {
    if (coords.touches && coords.touches.length > 1) return;
    // evt.stopPropagation();
    // evt.preventDefault();

    //      this.logEvent("contactEnd", evt);
    let redrawNeeded = false;
    // fire on selected element(s) only -- don't check intersection
    this.editables.forEach((editable) => {
      if (editable.selected) {
        redrawNeeded = editable.eventHandler.contactEventEnd() || redrawNeeded;
      }
    }, this);
    this.triggerListeners('contactEventEnd');
    if (redrawNeeded) this.redraw();
  }

  mouseOut(evt, coords) {
    log('editor mouseOut!!!!', evt);
    if (coords.touches && coords.touches.length > 1) return;
    // evt.stopPropagation();
    // evt.preventDefault();

    let redrawNeeded = false;
    //      this.logEvent("mouse out", evt);
    // check the mouse position and see if it's really out of the overall box
    // (this fires on all inner elements, too)

    /*      var divBox = this.vexFlowDiv.getBoundingClientRect();
    var outOfBounds = (evt.clientX <= divBox.left ||
      evt.clientY <= divBox.top ||
      evt.clientX >= divBox.right ||
      evt.clientY >= divBox.bottom)
    if(!outOfBounds) return;
*/
    // this.logEvent("mouseOut", evt);

    // if the mouse was down, fire contactEventCancel on selected objects
    /* if dragging, we go out of range first, so this doesn't matter...
    if (this.mouseDown) {
      this.editables.forEach((editable) => {
        if (editable.selected) {
          redrawNeeded = editable.eventHandler.contactEventEnd(coords) ||
          redrawNeeded;
        }
        if (editable.ghosting) {
          redrawNeeded = editable.eventHandler.mouseOut(coords) || redrawNeeded;
        }
      }, this);
    // otherwise, fire mouseOut on each priorIntersection:
    } else {
*/
    this.priorIntersections.forEach((intersect) => {
      redrawNeeded = intersect.eventHandler.mouseOut() || redrawNeeded;
    }, this);

    this.priorIntersections = [];
    this.triggerListeners('mouseOut');
    if (redrawNeeded) this.redraw();
  }

  hover(evt, coords) {
    if (coords.touches && coords.touches.length > 1) return;
    // evt.stopPropagation();
    // evt.preventDefault();

    let redrawNeeded = false;
    const intersections = this.getIntersections(coords);
    // log('intersections', intersections);
    const priorIntersections = this.priorIntersections;
    // log('priorIntersections', priorIntersections);

    // Check its previous intersections
    // on any new, fire mouseIn
    intersections.forEach((intersection) => {
      if (priorIntersections.indexOf(intersection) === -1) {
        redrawNeeded = !!intersection.eventHandler.mouseIn(coords) || redrawNeeded;
      }
    }, this);

    // on any that no longer intersect, fire mouseOut
    priorIntersections.forEach((priorIntersection) => {
      if (intersections.indexOf(priorIntersection) === -1) {
        redrawNeeded = priorIntersection.eventHandler.mouseOut() || redrawNeeded;
      }
    }, this);

    // now fire hover:
    intersections.forEach((intersection) => {
      redrawNeeded = !!intersection.eventHandler.hover(coords) || redrawNeeded;
    }, this);

    this.priorIntersections = intersections;
    this.priorEditables = [].concat(this.editables);
    if (redrawNeeded) this.redraw();
    this.triggerListeners('move');
  }

  drag(evt, coords) {
    log('drag', coords);
    if (coords.touches && coords.touches.length > 1) return;
    // evt.stopPropagation();
    // evt.preventDefault();

    let redrawNeeded = false;
    // Did anyone get the event? If not, fire on intersections instead of selected editables.
    let editableTargeted = false;
    const intersections = this.getIntersections(coords);
    // And now we can fire a drag:
    this.editables.forEach((editable) => {
      if (editable.selected) {
        editableTargeted = true;
        redrawNeeded = !!editable.eventHandler.drag(coords) || redrawNeeded;
      }
    }, this);

    if (!editableTargeted) {
      intersections.forEach((editable) => {
        redrawNeeded = !!editable.eventHandler.drag(coords);
      });
    }

    this.priorIntersections = intersections;
    this.priorEditables = [].concat(this.editables);
    if (redrawNeeded) this.redraw();
    this.triggerListeners('move');
  }

  delete() {
    log('Delete called!');
    log('Editables:');
    log(this.editables);
    let deleted = false;
    let listenerTriggered = false;
    this.editables.forEach((editable) => {
      if (editable.selected || editable.selectedNotes.length !== 0) {
        log('Calling event handler!');
        listenerTriggered = true;
        deleted = deleted || editable.eventHandler.delete();
      }
    }, this);
    if (!listenerTriggered) this.triggerListeners('delete', false);
    return deleted;
  }

  // # Event Dispatchers (external event listeners)

  addListener(type, callback, thisArg) {
    // adds function to be called back when status changes.
    if (typeof thisArg === 'undefined') thisArg = this;
    this.listeners.push({ type, callback: callback.bind(thisArg) });
  }

  triggerListeners(type, ...rest) {
    this.listeners.forEach((listener) => {
      if (listener.type === type) {
        listener.callback(...rest);
      }
    });
  }

  // selectNext() {
  //   const selected = this.editables.find(editable => editable.selected || editable.selectedNotes > 0);
  //   this.deselectAll();

  // }

  // selectPrevious() {

  // }

  /**
   *
   * @param accidental 'bb', 'b', 'n', '', '#' or 'x'
   */
  setAccidental(accidental) {
    // If any ghosting notes are set, don't set any selected notes...
    const pendings = this.editables.filter((editable) => editable.pendingNote);
    pendings.forEach((editable) => editable.eventHandler.setAccidental(accidental));
    if (pendings.length) return;

    this.editables.forEach((editable) => {
      if (editable.selected || editable.selectedNotes.length) {
        editable.eventHandler.setAccidental(accidental);
      }
    });
    this.renderer.render();
  }

  getSelectedEditables() {
    return this.editables.filter((editable) => editable.selected);
  }

  getSelected() {
    return this.editables
      .filter((editable) => editable.selected)
      .map((editable) => editable.noteHolder);
  }

  deselectAll(dontRedraw = false) {
    let redrawNeeded = false;
    this.editables.forEach((editable) => {
      if (editable.selected || editable.selectedNotes.length > 0) redrawNeeded = true;
      editable.unselect();
    });
    if (!dontRedraw) {
      this.keyboardFocusIndex = 0;
    }
    if (redrawNeeded && !dontRedraw) {
      this.redraw();
      return undefined;
    }
    return redrawNeeded;
  }

  exportSVG() {
    const s = new (window as any).XMLSerializer();
    return s.serializeToString(this.renderer.ctx.svg);
  }

  redraw() {
    this.renderer.render();
  }

  deactivate() {
    this.deselectAll();
    this.renderer.music.staves.forEach((staff) => {
      if (staff.customKeySignature) {
        staff.customKeySignature.lockSignature();
      }
    });
    this.editables.forEach((editable) => {
      // eslint-disable-next-line no-param-reassign
      delete editable.noteHolder.editable;
    });
    this.renderer.ctx.svg.setAttribute('aria-live', 'off');
    this.renderer.render();
    this.keyboardDisabled = true;
  }
}
